import React, { useEffect, useState, useContext, useRef } from "react";
import SearchModal from "../modals/SearchModal";
import { getSymbols } from "../utils/api";
import WebsocketWrapperContext from "../context/WebsocketWrapperContext";
import { useTheme } from "../context/ThemeContext";
import { useSearchParams } from "react-router-dom";
// Import SVG images
import upArrowSvg from "../images/Builder/global_market_pos.svg";
import downArrowSvg from "../images/Builder/global_market_neg.svg";
import SearchIcon from "../images/Builder/search_normal.svg";
let triggerSubscribe = true;
let connectionEstablished = false;

// Pre-defined symbols for the tabs
const DEFAULT_SYMBOLS = [
  { id: "nifty", name: "Nifty", scrip: "NIFTY" },
  { id: "banknifty", name: "Bank Nifty", scrip: "BANKNIFTY" },
  { id: "finnifty", name: "Fin Nifty", scrip: "FINNIFTY" },
  { id: "sensex", name: "Sensex", scrip: "SENSEX" },
  { id: "search", name: "Search", scrip: "" }, // Added Search tab
];

//React.memo => so the component will only re-render if its props change
const SymbolSelection = React.memo(
  ({ fetchDataForSymbol, updateSelectedSymbol }) => {
    const {
      checkWebsocketConnection,
      subscribeToSymbols,
      unsubscribeToSymbols,
      symbolList,
      symbolData,
    } = useContext(WebsocketWrapperContext);
    const [searchParams] = useSearchParams();

    const [selectedSymbol, setSelectedSymbol] = useState(null);
    const [underlyingCodes, setUnderlyingCodes] = useState([]);
    const [activeTab, setActiveTab] = useState("nifty");
    const [defaultSymbolsData, setDefaultSymbolsData] = useState({});
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredSymbols, setFilteredSymbols] = useState([]);
    const [isSearchActive, setIsSearchActive] = useState(false); // Track search state

    const prevUnderlyingCodeRef = useRef(null);
    const subscribedSymbolsRef = useRef(new Set());

    // Fetch all default symbols data
    useEffect(() => {
      const fetchDefaultSymbols = async () => {
        try {
          const symbols = await getSymbols();
          // console.log("Fetched symbols from API:", symbols);
          const symbolsMap = {};
          const symbolCodesToSubscribe = [];

          DEFAULT_SYMBOLS.forEach((defaultSymbol) => {
            if (defaultSymbol.id !== "search") {
              // Skip search since it's not a real symbol
              const foundSymbol = symbols.find(
                (symbol) => symbol.scrip === defaultSymbol.scrip
              );
              if (foundSymbol) {
                symbolsMap[defaultSymbol.id] = foundSymbol;
                symbolCodesToSubscribe.push(foundSymbol.code);
              }
            }
          });

          setDefaultSymbolsData(symbolsMap);

          // Set initial selected symbol if not already set
          if (!selectedSymbol && symbolsMap.nifty) {
            setSelectedSymbol(symbolsMap.nifty);
            updateSelectedSymbol(symbolsMap.nifty);
          }

          // Subscribe to all default symbols
          if (symbolCodesToSubscribe.length > 0) {
            connectionEstablished = checkWebsocketConnection();
            if (connectionEstablished) {
              subscribeToSymbols(symbolCodesToSubscribe, "NSE");
              symbolCodesToSubscribe.forEach((code) => {
                subscribedSymbolsRef.current.add(code);
              });
            }
          }
        } catch (error) {
          console.error("Error fetching default symbols:", error);
        }
      };

      fetchDefaultSymbols();

      // Cleanup function to unsubscribe when component unmounts
      return () => {
        const symbolsToUnsubscribe = Array.from(subscribedSymbolsRef.current);
        if (symbolsToUnsubscribe.length > 0) {
          unsubscribeToSymbols(symbolsToUnsubscribe, "NSE");
        }
      };
    }, []);

    useEffect(() => {
      const symbolFromUrl = searchParams.get("trading_symbol");
      // console.log("selected sym in url effect", selectedSymbol);

      if (symbolFromUrl && symbolFromUrl !== selectedSymbol?.scrip) {
        const fetchSymbols = async () => {
          try {
            const symbols = await getSymbols();
            const tradingSymbolFromURL = symbols.find(
              (symbol) => symbol.scrip === symbolFromUrl
            );
            if (tradingSymbolFromURL) {
              setSelectedSymbol(tradingSymbolFromURL);
              sessionStorage.setItem(
                "selectedSymbol",
                JSON.stringify(tradingSymbolFromURL)
              );
              subscribeToSymbols([tradingSymbolFromURL.code], "NSE");
            }
          } catch (error) {
            console.error("Error fetching symbols:", error);
          }
        };
        fetchSymbols();
      }
    }, [searchParams]);

    useEffect(() => {
      const storedSymbol = sessionStorage.getItem("selectedSymbol");

      // Only proceed if we're not in active search mode
      if (!isSearchActive) {
        if (storedSymbol) {
          const parsedSymbol = JSON.parse(storedSymbol);
          setSelectedSymbol(parsedSymbol);
          updateSelectedSymbol(parsedSymbol);

          // Set active tab based on the stored symbol if it's one of the default ones
          const defaultSymbol = DEFAULT_SYMBOLS.find(
            (sym) => sym.scrip === parsedSymbol.scrip
          );
          if (defaultSymbol) {
            setActiveTab(defaultSymbol.id);
          } else {
            setActiveTab("search");
          }
          // Always ensure search mode is false when loading from storage
          setIsSearchActive(false);
        } else {
          const fetchSymbols = async () => {
            const symbols = await getSymbols();
            if (symbols) {
              const niftySymbol = symbols.find(
                (symbol) => symbol.scrip === "NIFTY"
              );
              if (niftySymbol) {
                setSelectedSymbol(niftySymbol);
                updateSelectedSymbol(niftySymbol);
                setActiveTab("nifty");
                setIsSearchActive(false);
                sessionStorage.setItem(
                  "selectedSymbol",
                  JSON.stringify(niftySymbol)
                );
              }
            }
          };
          fetchSymbols();
        }
      }
    }, [updateSelectedSymbol, isSearchActive]); // Add isSearchActive to dependencies
    const handleSymbolSelection = (symbol) => {
      if (!symbol) return;

      // Subscription logic remains the same...
      subscribeToSymbols([symbol.code], symbol.exchange || "NSE");
      subscribedSymbolsRef.current.add(symbol.code);

      setSelectedSymbol(symbol);
      sessionStorage.setItem("selectedSymbol", JSON.stringify(symbol));
      updateSelectedSymbol(symbol);

      // Underlying codes logic remains the same...
      if (prevUnderlyingCodeRef.current !== symbol.underlying_code) {
        setUnderlyingCodes([symbol.underlying_code]);
        prevUnderlyingCodeRef.current = symbol.underlying_code;
      }

      // Only switch tabs if not in search mode
      if (!isSearchActive) {
        const defaultSymbolTab = DEFAULT_SYMBOLS.find(
          (defSymbol) => defSymbol.scrip === symbol.scrip
        );
        setActiveTab(defaultSymbolTab ? defaultSymbolTab.id : "search");
      }

      // Always exit search mode after selection
      setIsSearchActive(false);
    };

    const handleTabClick = (tabId) => {
      if (tabId === "search") {
        if (activeTab === "search") {
          // Toggle search mode if already on search tab
          setIsSearchActive(!isSearchActive);
        } else {
          // First click on search tab
          setIsSearchActive(true);
        }
        setActiveTab("search");
        setFilteredSymbols([]);
        setSearchTerm("");

        // Clear the selected symbol from session storage when entering search mode
        sessionStorage.removeItem("selectedSymbol");
      } else {
        // For other tabs
        setIsSearchActive(false);
        setActiveTab(tabId);
        if (defaultSymbolsData[tabId]) {
          handleSymbolSelection(defaultSymbolsData[tabId]);
        }
      }
    };

    const handleSearchIconClick = () => {
      setIsSearchActive(true);
      setFilteredSymbols([]);
      setSearchTerm("");
      setActiveTab("search");
      // Clear the selected symbol from session storage when activating search
      sessionStorage.removeItem("selectedSymbol");
    };

    // Modify the render condition to be more explicit

    // Check websocket connection periodically and resubscribe if needed
    useEffect(() => {
      const intervalId = setInterval(() => {
        connectionEstablished = checkWebsocketConnection();
        if (connectionEstablished && subscribedSymbolsRef.current.size > 0) {
          // Resubscribe to all symbols if connection was lost and reestablished
          const allSymbolCodes = Array.from(subscribedSymbolsRef.current);
          subscribeToSymbols(allSymbolCodes, "NSE");
        }
      }, 10000);

      return () => clearInterval(intervalId);
    }, [checkWebsocketConnection, subscribeToSymbols]);

    // Get the first letter of the selected symbol
    const getFirstLetter = (symbol) => {
      if (!symbol || !symbol.scrip) return "";
      return symbol.scrip.charAt(0);
    };

    // Render the trend arrow image based on change value
    const renderTrendArrow = (change) => {
      if (change === undefined) return null;

      if (change > 0) {
        return (
          <img
            src={upArrowSvg}
            alt="Up"
            className="trend-arrow up"
            width="16"
            height="16"
          />
        );
      } else {
        return (
          <img
            src={downArrowSvg}
            alt="Down"
            className="trend-arrow down"
            width="16"
            height="16"
          />
        );
      }
    };

    // Render the current tab's data
    const renderSymbolData = (symbolId) => {
      if (symbolId === "search" || !defaultSymbolsData[symbolId]) {
        return null;
      }

      const symbol = defaultSymbolsData[symbolId];
      const isActive = activeTab === symbolId;

      if (!symbol || !symbol.code) {
        return <div className="loading-data">Loading data...</div>;
      }

      const symbolLtpValue = symbolData[symbol.code]?.ltp;
      const symbolChange = symbolData[symbol.code]?.change;
      const symbolChangePer = symbolData[symbol.code]?.change_per;

      if (!symbolLtpValue) {
        return <div className="loading-data">Loading price data...</div>;
      }

      return (
        <div className={`symbol-data ${isActive ? "active" : ""}`}>
          <div className="symbol-title">
            <h2 className="tw-flex tw-items-center">
              {symbol.scrip}{" "}
              <span className="ms-2 trend-arrow-container">
                {renderTrendArrow(symbolChange)}
              </span>
            </h2>
            <div className="symbol-price-container">
              <div className="price-value">
                {typeof symbolLtpValue === "string" &&
                symbolLtpValue.includes(".") ? (
                  <>
                    {
                      parseFloat(symbolLtpValue)
                        .toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                        .split(".")[0]
                    }
                    .
                    <span style={{ fontWeight: 400 }}>
                      {
                        parseFloat(symbolLtpValue)
                          .toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                          .split(".")[1]
                      }
                    </span>
                  </>
                ) : (
                  symbolLtpValue && (
                    <>
                      {
                        parseFloat(symbolLtpValue)
                          .toLocaleString("en-IN")
                          .split(".")[0]
                      }
                      .
                      <span style={{ fontWeight: 400 }}>
                        {
                          parseFloat(symbolLtpValue)
                            .toLocaleString("en-IN")
                            .split(".")[1]
                        }
                      </span>
                    </>
                  )
                )}
              </div>
              {symbolChange !== undefined && (
                <span
                  className="change-value"
                  style={{
                    color: symbolChange > 0 ? "#5CA81D" : "#DD6565",
                  }}>
                  <span className="change-arrow">
                    {symbolChange > 0 ? "▲" : "▼"}
                  </span>
                  {symbolChange > 0 ? "+" : ""}
                  {String(symbolChange).split(".")[0]}.
                  <span>{String(symbolChange).split(".")[1]}</span>(
                  {String(symbolChangePer).split(".")[0]}.
                  <span>{String(symbolChangePer).split(".")[1]}</span>
                  %)
                </span>
              )}
            </div>
          </div>
        </div>
      );
    };

    const renderSearchSymbol = () => {
      if (!selectedSymbol) return null;

      return (
        <div
          className="symbol-data active flex items-center"
          style={{ width: "100%" }}>
          <div className="symbol-title flex-grow-1">
            <h2>
              {selectedSymbol.scrip}{" "}
              <span className="ms-2 trend-arrow-container">
                {selectedSymbol.code &&
                  symbolData[selectedSymbol.code] &&
                  renderTrendArrow(symbolData[selectedSymbol.code]?.change)}
              </span>
            </h2>

            {selectedSymbol.code && symbolData[selectedSymbol.code] && (
              <div className="symbol-price-container">
                <div className="price-value">
                  {typeof symbolData[selectedSymbol.code]?.ltp === "string" &&
                  symbolData[selectedSymbol.code]?.ltp.includes(".") ? (
                    <>
                      {
                        parseFloat(symbolData[selectedSymbol.code]?.ltp)
                          .toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                          .split(".")[0]
                      }
                      .
                      <span style={{ fontWeight: 400 }}>
                        {
                          parseFloat(symbolData[selectedSymbol.code]?.ltp)
                            .toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                            .split(".")[1]
                        }
                      </span>
                    </>
                  ) : (
                    symbolData[selectedSymbol.code]?.ltp && (
                      <>
                        {
                          parseFloat(symbolData[selectedSymbol.code]?.ltp)
                            .toLocaleString("en-IN")
                            .split(".")[0]
                        }
                        .
                        <span style={{ fontWeight: 400 }}>
                          {
                            parseFloat(symbolData[selectedSymbol.code]?.ltp)
                              .toLocaleString("en-IN")
                              .split(".")[1]
                          }
                        </span>
                      </>
                    )
                  )}
                </div>
                {symbolData[selectedSymbol.code]?.change !== undefined && (
                  <span
                    className="change-value"
                    style={{
                      color:
                        symbolData[selectedSymbol.code]?.change > 0
                          ? "#5CA81D"
                          : "#DD6565",
                    }}>
                    {/* {renderTrendArrow(symbolData[selectedSymbol.code]?.change)} */}
                    <span className="change-arrow">
                      {symbolData[selectedSymbol.code]?.change > 0 ? "▲" : "▼"}
                    </span>
                    {symbolData[selectedSymbol.code]?.change > 0 ? "+" : ""}
                    {
                      String(symbolData[selectedSymbol.code]?.change).split(
                        "."
                      )[0]
                    }
                    .
                    <span>
                      {
                        String(symbolData[selectedSymbol.code]?.change).split(
                          "."
                        )[1]
                      }
                    </span>
                    (
                    {
                      String(symbolData[selectedSymbol.code]?.change_per).split(
                        "."
                      )[0]
                    }
                    .
                    <span>
                      {
                        String(
                          symbolData[selectedSymbol.code]?.change_per
                        ).split(".")[1]
                      }
                    </span>
                    %)
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Search icon */}
          <div
            className="search-icon-container"
            onClick={handleSearchIconClick}
            style={{
              cursor: "pointer",
            }}>
            <img src={SearchIcon} alt="" height={24} width={24} />
          </div>
        </div>
      );
    };

    const renderSearchInput = () => {
      return (
        <div
          className="search-dropdown-container p-0"
          style={{ position: "relative", width: "100%" }}>
          {/* SVG Icon - Positioned absolutely inside the input */}
          <img src={SearchIcon} alt="search" className="tab-search-icon" />

          {/* Search Input */}
          <input
            type="text"
            className="form-control search-symbol-input no-focus-outline"
            placeholder="Search underlying instruments, etc..."
            value={searchTerm}
            onChange={async (e) => {
              const term = e.target.value;
              setSearchTerm(term);

              if (term.trim() === "") {
                setFilteredSymbols([]);
                return;
              }

              try {
                const allSymbols = await getSymbols();
                const matches = allSymbols.filter((symbol) =>
                  symbol.scrip.toLowerCase().includes(term.toLowerCase())
                );
                setFilteredSymbols(matches);
              } catch (error) {
                console.error("Failed to fetch symbols:", error);
              }
            }}
          />

          {/* Dropdown results */}
          {filteredSymbols.length > 0 && (
            <ul
              className="dropdown-menu show"
              style={{
                position: "absolute",
                width: "100%",
                top: "100%",
                zIndex: 9999,
                maxHeight: "300px",
                overflowY: "auto",
              }}>
              {filteredSymbols.map((symbol) => (
                <li key={symbol.code} className="dropdown-item p-2">
                  <button
                    onClick={() => {
                      handleSymbolSelection(symbol);
                      setFilteredSymbols([]);
                      setSearchTerm("");
                      setIsSearchActive(false);
                    }}
                    className="w-100 text-start flex justify-content-between items-center border-0 bg-transparent">
                    <div>
                      <div className="dropdown-scrip-name">{symbol.scrip}</div>
                      {/* <div className="text-muted small">{symbol.name}</div> */}
                    </div>
                    <span className="dropdown-scrip-badge">
                      {symbol.exchange}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    };

    return (
      <div className="symbol-selection-container">
        {/* Tab Navigation */}
        <div className="symbol-tabs-wrapper">
          <ul className="symbol-tabs">
            {DEFAULT_SYMBOLS.map((symbol) => (
              <li
                key={symbol.id}
                className={`symbol-tab ${activeTab === symbol.id ? "active" : ""}`}
                onClick={() => handleTabClick(symbol.id)}>
                {symbol.name}
              </li>
            ))}
          </ul>
        </div>

        {/* Symbol Content */}
        <div className="symbol-content flex gap-2 items-center">
          {isSearchActive ? (
            ""
          ) : (
            <div className="symbol-first-letter flex items-center justify-center">
              <span>{getFirstLetter(selectedSymbol)}</span>
            </div>
          )}

          {/* Conditional rendering based on active tab and search state */}
          {activeTab === "search"
            ? isSearchActive
              ? renderSearchInput()
              : renderSearchSymbol()
            : renderSymbolData(activeTab)}
        </div>
      </div>
    );
  }
);

export default SymbolSelection;
